import React, { useState } from 'react';
import { cases as casesApi, errorMessage } from '../utils/api';
import { formatDateTime } from '../utils/format';

/*
 * یادداشت‌های پرونده.
 *
 * یک رشته‌ی مشترک بین همه‌ی نقش‌هاست: پذیرش هنگام تشکیل پرونده یادداشت پایه را
 * می‌گذارد و هر مرحله‌ی بعدی چیزی به آن اضافه می‌کند. یادداشت‌ها ویرایش نمی‌شوند
 * — همان قاعده‌ای که در کل سامانه بر تاریخچه حاکم است. فقط مدیر می‌تواند یکی را
 * حذف کند.
 */

const COLLAPSE_AFTER = 2;

function NoteItem({ note, onRemove, busy }) {
  return (
    <li className={`note-item ${note.is_initial ? 'note-item--initial' : ''}`}>
      <div className="note-item__meta">
        <span className="note-item__who"><bdi>{note.author_name}</bdi></span>
        <span className="note-item__role">{note.author_role_display}</span>
        <time>{formatDateTime(note.created_at)}</time>
        {note.is_initial && <span className="note-item__tag">یادداشت پایه</span>}
        {onRemove && (
          <button
            type="button"
            className="note-item__del"
            onClick={() => onRemove(note)}
            disabled={busy}
            aria-label="حذف یادداشت"
            title="حذف یادداشت"
          >
            ✕
          </button>
        )}
      </div>
      <p className="note-item__text"><bdi>{note.text}</bdi></p>
    </li>
  );
}

export default function CaseNotes({ caseId, notes, onChanged, canDelete = false, disabled = false }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [writing, setWriting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const list = notes || [];
  // یادداشت پایه همیشه دیده می‌شود؛ آنچه پنهان می‌ماند، میانه‌ی رشته است.
  const hidden = Math.max(list.length - COLLAPSE_AFTER, 0);
  const visible = expanded || hidden === 0 ? list : list.slice(-COLLAPSE_AFTER);

  const submit = async (event) => {
    event.preventDefault();
    const value = text.trim();
    if (!value) return;

    setBusy(true);
    setError('');
    try {
      await casesApi.addNote(caseId, value);
      setText('');
      setWriting(false);
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, 'یادداشت ثبت نشد.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (note) => {
    setBusy(true);
    setError('');
    try {
      await casesApi.removeNote(caseId, note.id);
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="notes-box">
      <div className="notes-box__head">
        <span className="notes-box__label">
          🗒️ یادداشت‌ها
          {list.length > 0 && <span className="notes-box__count num">{list.length}</span>}
        </span>
        {!disabled && !writing && (
          <button type="button" className="notes-box__add" onClick={() => setWriting(true)}>
            ＋ افزودن
          </button>
        )}
      </div>

      {hidden > 0 && !expanded && (
        <button type="button" className="notes-box__more" onClick={() => setExpanded(true)}>
          نمایش {hidden} یادداشت قدیمی‌تر
        </button>
      )}

      {list.length > 0 && (
        <ul className="notes-box__list">
          {visible.map((note) => (
            <NoteItem
              key={note.id}
              note={note}
              busy={busy}
              onRemove={canDelete ? remove : null}
            />
          ))}
        </ul>
      )}

      {list.length === 0 && !writing && (
        <p className="notes-box__empty">یادداشتی ثبت نشده است.</p>
      )}

      {writing && (
        <form className="notes-box__form" onSubmit={submit}>
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="یادداشت خود را بنویسید…"
            rows={3}
            maxLength={4000}
            autoFocus
            disabled={busy}
          />
          <div className="notes-box__actions">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setWriting(false);
                setText('');
                setError('');
              }}
              disabled={busy}
            >
              انصراف
            </button>
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={busy || !text.trim()}
            >
              {busy ? 'در حال ثبت…' : 'ثبت یادداشت'}
            </button>
          </div>
        </form>
      )}

      {error && <p className="field__error">{error}</p>}
    </section>
  );
}
